import { AttachmentFile } from "apis/attachment_files";

export interface Me {
  user: {
    dynamo_id: string
    email: string
    twitter_icon_image_url: string
    mail_settings: MailSettings
    push_settings: PushSettings
    new_email: string | null
    channeltalk_id: string
    postal_code: string | null // 口座情報と同じ
    address: string | null // 口座情報と同じ
    birth_date: string | null
    phone_number: string | null
    personal_first_name: string | null
    personal_last_name: string | null
    personal_first_name_kana: string | null
    personal_last_name_kana: string | null
    tax_exemption: boolean | null
    invoice_registration_name: string | null
    invoice_registration_number: string | null
    accepted_privacy_policy: boolean
    accepted_terms_of_service: boolean
    inquiry_id: string
    // 口座情報
    bank_info: {
      first_name: string | null
      last_name: string | null
      first_name_kana: string | null
      last_name_kana: string | null
      postal_code: string | null
      address: string | null
      bank_code: string | null
      bank_name: string | null
      bank_branch_name: string | null
      bank_branch_code: string | null
      bank_account_type: number | null
      bank_account_number: string | null
      bank_account_name: string | null
    } | null
  }
  user_public: {
    dynamo_id: string
    twitter_screen_name: string
    twitter_username: string
    door_screen_name: string
    icon_image_url: string
    header_image_url: string
    description: string
  }
  has_door: boolean
  has_staff: boolean
}

export interface MyDoor {
  door: DoorDetail | null
  owner: User
}
export interface Review {
  body: string
  entried_at: number
  icon_image_url: string | null
  username: string
}
export interface ReviewForOwner extends Review {
  id: number
  pinned_at: number | null
  posted_at: number | null
  visible: boolean
}

export interface PushSettings {
  [key: string]: any
}

export interface MailSettings {
  [key: string]: any
}

export interface WriterUserPublic {
  icon: string
  name: string
}

const defaultWriterUserPublicValue = {
  icon: '',
  name: ''
}

export interface DoorTop {
  owned: boolean
  invalid_plan_secret_signature: boolean
  can_publish?: boolean // オーナーorスタッフの場合のみ
  owner_profile_filled?: boolean // オーナーorスタッフの場合のみ
  profile_filled?: boolean // オーナーorスタッフの場合のみ
  tax_exemption?: null | boolean
  door: DoorDetail | null
  owner: User
  staff: string | null
}

export interface DoorDetail {
  force_view: boolean
  sub_id: string
  title: string
  subscription_cost: number
  description: string
  door_recommends: string[]
  free_trial_period: number | null
  member_count: number | null
  latest_post_updated_at: string | null
  post_count: number
  subscribing: boolean
  external_video_link: string
  door_images: Array<{
    image_url: string
    description: string
    created_at: string
  }>
  sample_posts: Array<SamplePost>
  door_owners: Array<{ image_url: string }>
  subscription_plans: Array<SubscriptionPlanV1>
  keywords: Array<Keyword>
  customize: boolean
  status?: 'in_private' | 'published' // オーナーの場合のみ
  is_display_member_count: boolean
  lottery?: {
    id: number
    header_image?: AttachmentFile
  }
}

export interface SubscriptionPlan {
  id: number
  title: string
  description: string
  subscription_cost: number
  is_free_trial_available: boolean
  free_trial_period: boolean
  thumnail_image: string
  is_subscribing?: boolean
  is_capacity_limit: boolean
  joinable_starts_at?: string
  is_reserving: boolean
  is_suspended: boolean
}

export interface SubscriptionPlanV1 {
  id: number
  title: string
  description: string
  thumnail_image: string | undefined
  subscription_cost: number
  capacity: number | null
  free_trial_period: number | null
  remaining_capacity: number | null
  is_capacity_limit: boolean
  is_display_capacity: boolean
  is_display_member_count: boolean
  member_count: number
  reservation_count: number
  is_subscribing?: boolean
  is_reserving: boolean
  is_subscribed?: boolean
  is_free_trial_available: boolean
  is_suspended: boolean
  recurring: boolean
  payment_interval_months: number
  reservable: boolean
  joinable_starts_at?: string
  joinable_months?: number
  available_days_after_joinable?: number
  rooms: Array<{
    id: number
    title: string
  }>
  hash: string
  secret_id: string | undefined
  invisible: boolean
}

export interface User {
  suspended: boolean
  name: string
  id: string
  description: string
  header: string
  icon: string
}

export interface Doors {
  current_page: number
  total_pages: number
  total_count?: number
  doors: Array<Door>
}
export interface Door {
  title: string
  username: string
  id: string
  header: string
  icon: string
  free_trial_period?: number
  customize: boolean
  is_published: boolean
}
export interface Keyword {
  id: number
  word: string
  word_en: string
}

export interface Banner {
  title: string
  url: string
  image_url: string
}

export interface SamplePostForTop {
  title: string
  content: string
  image: string
  attached_files: SamplePostAttachedFiles
  video: string | null
  display_order: number
  door: {
    title: string
  }
  user: {
    name: string
    icon: string
    url: string
    twitter_screen_name: string
  }
}

export interface SamplePostAttachedFiles {
  images: Array<{
    id: number
    url: string
  }>
  videos: Array<{
    url: string
  }>
  audios: Array<AttachedFile>
  documents: Array<AttachedFile>
  files: Array<AttachedFile>
}

const defaultSamplePostAttachedFilesValue: SamplePostAttachedFiles = {
  images: [],
  videos: [],
  audios: [],
  documents: [],
  files: []
}

export interface SamplePost {
  id: number
  title: string
  content: string
  content_char_count: number
  image_url: string | null
  image_url_orig: string | null
  video_url: string | null
  dynamo_id: string
  post_created_at: string
  comment_count: number
  like_count: number
  room_id: number
  attached_files: SamplePostAttachedFiles
  writer_user_public: WriterUserPublic
  character_limit: number | null
  is_restricted: boolean
}

export const defaultSamplePostValue: SamplePost = {
  id: 0,
  title: '',
  content: '',
  content_char_count: 0,
  image_url: null,
  image_url_orig: null,
  video_url: null,
  dynamo_id: '',
  post_created_at: '',
  comment_count: 0,
  like_count: 0,
  room_id: 0,
  attached_files: defaultSamplePostAttachedFilesValue,
  writer_user_public: defaultWriterUserPublicValue,
  character_limit: null,
  is_restricted: false
}

export interface Top {
  pickup: Array<Door>
  recommend: Array<Door>
  free: Array<Door>
  sample_posts: Array<SamplePostForTop>
  keywords: Array<Keyword>
}

export interface FirstView {
  urgent: Urgent
  special_banners?: Array<Banner>
  creditcard_alert?: string
  opened_salon_volume: number
  billed_volume: number
  carousel_banners?: Array<Banner>
}

export interface Urgent {
  body: string
  urgency: 'attention' | 'caution' | 'warning'
}

export type StaffPosition = 'admin_owner' | 'general_owner' | 'general' | null
export interface Rooms {
  door: {
    title: string
    username: string
    id: string
    header: string
    icon: string
    free_trial_period?: number
    customize: boolean
    is_published: boolean
    lottery?: {
      id: number
      header_image?: AttachmentFile
      event_status: LotteryEventStatus
    }
  }
  owner: boolean
  staff: StaffPosition
  rooms: Room[]
  private_rooms: Room[]
  need_sample_post_reminder: boolean
  needs_resubmission: boolean
}
export interface RoomsMe {
  door: Door
  owner: boolean
  staff: string | null
  rooms: MeRoom[]
}

interface RoomBase {
  id: number
  title: string
}
export interface Room extends RoomBase {
  unread: boolean
  browsable: boolean
  current_user_commentable: boolean
  current_user_postable: boolean
  invited: boolean
  is_private: boolean
}

export interface RoomDetail extends RoomBase {
  post_data: Posts
}

export interface Posts {
  current_page: number
  total_pages: number
  posts: Post[]
}

export interface Image {
  id: number
  url: string
  token?: string
}

export interface AttachedFile {
  id: number
  filename: string
  url: string
}

export interface AttachedFiles {
  images: Array<Image>
  audios: Array<AttachedFile>
  documents: Array<AttachedFile>
  files: Array<AttachedFile>
}

export type Ogp = {
  url: string
  og_title: string
  og_url: string
  og_iamge: string
  og_description: string
  og_site_name: string
}

export type PostStatus = 'published' | 'wait_for_encode' | 'banned' | 'ready_to_publish' | 'fail_encode'

export type Post = {
  account_label: string | null
  title: string
  content: string
  is_private: boolean
  poster: Poster
  status: PostStatus
  created_at: string
  dynamo_id: string
  room: Omit<RoomBase, 'subscription_plans'>
  like: Like
  comment_count: number
  owned: boolean
  owned_by_relations: boolean
  deletable: boolean
  is_registered_sample_post: boolean
  bookmark: {
    id: number
    bookmarked: boolean
    bookmark_category_ids: number[] | null
  }
  images: Array<{
    url_private: string
    url_private_orig: string
  }> | null
  attached_files: AttachedFiles
  door: {
    customize: boolean
  }
  image_key: string | null
  videos: Array<{
    url?: string
    presigned_url?: string
    filename?: string
    id?: number
  }>
  pinned_to_home_at: string | null
  pinned_to_room_at: string | null
  ogps?: Array<Ogp>
  is_draft?: boolean
  scheduled_at?: string | null
  published_at: string | null
}

export interface Like {
  count: number
  is_liked: boolean
  users: Poster[]
}

export interface Poster {
  door_screen_name: string
  icon_image_url: string
  twitter_screen_name: string
}

export interface UploadFile {
  filename: string
  presigned_url: string
}

interface UploadFiles {
  wait_for_upload: boolean
  files: Array<UploadFile>
}
export interface PendingUploadAttachedFiles {
  upload_required: boolean
  images: {
    wait_for_upload: boolean
    presigned_urls: Array<string>
  }
  videos: {
    wait_for_upload: boolean
    presigned_urls: Array<string>
    keys: Array<string>
  }
  audios: UploadFiles
  documents: UploadFiles
  files: UploadFiles
}
export interface CreatePostResponse {
  id: string
  dynamo_id: string
  attached_files: PendingUploadAttachedFiles
  videos: Array<{ presigned_url: string }>
}
export interface UpdatePostResponse extends CreatePostResponse {}

interface CommentBase {
  id: number
  content: string
  status: 'published' | 'wait_for_encode' | 'banned'
  user?: Poster
  created_at?: string
  images: Array<Image>
  videos: Array<{ url: string }>
  attached_files: AttachedFiles
  like: Like
  account_label: string | null
  deleted: boolean
  is_private: boolean
  ogps?: Array<Ogp>
}
export interface Comment extends CommentBase {
  reply_comment_count: number
}

export interface ReplyComment extends CommentBase {
  user: Poster
  created_at: string
}
export interface MeRoom {
  id: number
  title: string
  subscription_plans: MeSubscriptionPlan[]
  permission: {
    member: {
      post_restrict: boolean
      comment_restrict: boolean
    }
    invitation_member: {
      post_restrict: boolean
      comment_restrict: boolean
    }
    staff: {
      post_restrict: boolean
      comment_restrict: boolean
    }
  }
}
export interface MeSubscriptionPlan {
  id: number
  title: string
  description: string
  subscription_cost: number
  deletable: boolean
  payment_interval_months: number
  recurring: boolean
}

export interface TransferRequest {
  amount: number
  created_at: string
}

export interface Feature {
  name: string
  content: string | null
  header_image: string
  header_image_sp: string
  doors: Door[]
}
export interface UserCredential {
  id: number
  email_verified: boolean
  email: string | null
  username: string
  passwordHash: string
  twitterId: string | null
  twitterIdentityId: string | null
  screen_name: string | null
  googleId: string | null
  googleIdentityId: string | null
  lineId: string | null
  lineIdentityId: string | null
  facebookId: string | null
  facebookIdentityId: string | null
  verifyEmailToken: string | null
}

export interface CognitoSigninToken {
  username: string
  email: string | null
  email_verified: boolean
  accessToken: string
  expiresIn: number
  tokenType: string
  refreshToken: string
  idToken: string
  userData: {
    UserAttributes: Array<{
      Name: string
      Value: string
    }>
    Username: string
  }
}

export interface UserWithdrawal {
  subscribed: boolean
  owner: boolean
}

export interface CategoryFaq {
  id: string
  slug: string
  order: number
  category: string
}

export interface Faq {
  id: string
  question: string
  order: number
  answer: string
  categories: CategoryFaq[]
}

export interface HacoMeta {
  total: number
  limit: number
  offset: number
}

export interface CategoryFaqs {
  meta: HacoMeta
  data: CategoryFaq[]
}

export interface Faqs {
  meta: HacoMeta
  data: Faq[]
}

export interface GoogleTokens {
  idToken: string
  accessToken: string
}

export interface LineTokens {
  idToken: string
  accessToken: string
}

export interface FacebookCredential {
  accessToken: string
  user: {
    name: string
    email: string
  }
}

export interface Page {
  id: string
  slug: string
  title: string
  entry: string
}

export interface Pages {
  meta: HacoMeta
  data: Page[]
}

export interface BookmarkCategory {
  id: number
  name: string
}

export interface BookmarkCategoryWithPosts {
  id: number
  name: string
  posts: Array<Post>
}

export interface Bookmarks {
  all_bookmark_posts: Array<Post>
  bookmark_categories: Array<BookmarkCategoryWithPosts>
}

export interface Supports {
  meta: HacoMeta
  data: Support[]
}

export interface CategorySupports {
  meta: HacoMeta
  data: CategorySupport[]
}

export interface Support {
  id: string
  slug: string
  title: string
  order: string
  category: CategorySupport[]
  ogp: string
  description: string
  paid: '無料' | '有料'
  url: string | null
  main: string
}

export interface CategorySupport {
  id: string
  category: string
  order: number
}

export type LotteryEventStatus = "before_holding" | "in_holding" | "after_holding"

export type Pagination = {
  total_count: number
  total_pages: number
  current_page: number
  begin_index: number
  end_index: number
}

export type ApiError = {
  error_code: string
}

export type PaymentError = ApiError & {
  error_code: 'PAYMENT_CARD_INFO_ERROR' | 'PAYMENT_ERROR' | 'PAYMENT_CARD_KIND_ERROR' | 'PAYMENT_CARD_FUDS_ERROR' | 'PAYMENT_3DS_UNSUPPORTED_ERROR' | 'PAYMENT_3DS_AUTHENTICATION_ERROR'
}

export type PlanError = ApiError & {
  error_code: 'PLAN_IS_SUSPENDED' | 'PLAN_ALREADY_SUBSCRIBED' | 'PLAN_IS_NOT_JOINABLE' | 'PLAN_HAS_BEEN_UPDATED' | 'PLAN_CAPACITY_IS_FULL'
}

export type LotteryError = ApiError & {
  error_code: 'OUT_OF_STOCK' | 'NOT_IN_HOLDING'
}

export type LotteryItemError = ApiError & {
  error_code: 'LOTTERY_ITEM_ALREADY_RECEIVED' | 'LOTTERY_ITEM_IS_ASSIGNING'
}

export type LotteryTermsError = ApiError & {
  error_code: 'NO_OWNER_LOTTERY_TERMS_OF_SERVICE_AGREEMENT' | 'NO_LOTTERY_TERMS_OF_SERVICE_AGREEMENT'
}